import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { ApiConflictError, ApiError } from '@/lib/api-client';

import {
  approveSections,
  getExtractionProposal,
  saveSectionEdit,
  saveTechSpecMarkdown,
  type SectionChangeKind,
  type SectionEdit,
} from './api';
import type { ExtractionProposal } from './types';
import { productEditKey } from '@/features/product/hooks';
import type { ProductEditSnapshot } from '@/features/product/api';

const proposalKey = (productId: number) =>
  ['extraction-proposal', productId] as const;

export function useExtractionProposal(productId: number | null) {
  const [timedOut, setTimedOut] = useState(false);
  const pollsRef = useRef(0);
  const lastAtRef = useRef(0);
  const pidRef = useRef(productId);

  // Reset give-up tracking synchronously when the product changes (React's
  // "reset state on prop change" pattern), so a stale timed-out state never leaks
  // onto the next product's fresh build — not even for a single render.
  if (pidRef.current !== productId) {
    pidRef.current = productId;
    pollsRef.current = 0;
    lastAtRef.current = 0;
    setTimedOut(false);
  }

  const query = useQuery<ExtractionProposal>({
    queryKey:
      productId == null ? ['extraction-proposal', 'none'] : proposalKey(productId),
    queryFn: () => {
      if (productId == null) throw new Error('productId is null');
      return getExtractionProposal(productId);
    },
    enabled: productId != null,
    staleTime: 0,
    // Poll while pending, but STOP after max_polls (a dead worker / giant-doc hang
    // must surface a give-up state, not poll forever). Gate on the SAME
    // component-local counter as the give-up flag, so a remount can't leave
    // polling stopped while the flag resets (a cache-persisted counter would).
    refetchInterval: (q) => {
      const data = q.state.data;
      if (data?.status !== 'pending') return false;
      return pollsRef.current < data.max_polls ? data.poll_ms : false;
    },
    refetchIntervalInBackground: false,
    // Polling pauses while backgrounded, so an extraction that finishes then
    // must be caught on refocus — otherwise the page sits on "processing".
    // (staleTime:0 already re-checks on open/click-in; this covers focus.)
    // BUT once ready/approved the proposal is immutable AND carries the multi-MB
    // source_markdown, so refetching it on every focus needlessly re-pulls the
    // whole document. Refetch on focus only while it can still change (pending →
    // catch the pending→ready flip; failed/awaiting_upload are cheap).
    refetchOnWindowFocus: (q) =>
      q.state.data?.status === 'ready' || q.state.data?.status === 'approved'
        ? false
        : 'always',
  });

  // Count each poll (success OR error) while pending; at max_polls surface the
  // give-up state. Counting error updates too means a proposal that starts 404ing
  // mid-poll still reaches the give-up path instead of spinning forever.
  useEffect(() => {
    const data = query.data;
    const at = Math.max(query.dataUpdatedAt, query.errorUpdatedAt);
    if (!at || at === lastAtRef.current) return;
    lastAtRef.current = at;
    if (data?.status === 'pending') {
      pollsRef.current += 1;
      if (data.max_polls && pollsRef.current >= data.max_polls) setTimedOut(true);
    } else if (data) {
      // reached a terminal state (ready/failed/approved/awaiting_upload)
      pollsRef.current = 0;
      setTimedOut(false);
    }
  }, [query.dataUpdatedAt, query.errorUpdatedAt, query.data]);

  return { ...query, timedOut };
}

/** Save an edit to a section-approved product's spec from the edit page. Deliberately does
 *  NOT invalidate the edit snapshot on success — a refetch would replace the markdown prop
 *  and stomp the reviewer's in-progress local edits; local state is the source of truth
 *  while editing. Invalidates the products list only, so list views stay fresh. */
export function useSaveTechSpecMarkdown(productId: number | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { markdown: string; changeKind: SectionChangeKind; ifMatch: string }) => {
      if (productId == null) throw new Error('productId is null');
      return saveTechSpecMarkdown(productId, vars.markdown, vars.changeKind, vars.ifMatch);
    },
    onSuccess: (result, vars) => {
      // Keep the edit-page snapshot in sync with the save so navigating away and back shows the
      // edited spec. The edit query key (['product-edit', id]) isn't under ['products'], so the
      // list-only invalidate never refreshed it — and StructuredSpecView won't re-sync a changed
      // `markdown` prop mid-mount (an editor must not reset on a background refetch). So write the
      // fresh markdown + updated_at straight into the cache (mirrors useAmendProduct).
      if (productId != null) {
        queryClient.setQueryData<ProductEditSnapshot>(productEditKey(productId), (old) =>
          old ? { ...old, tech_spec_markdown: vars.markdown, updated_at: result.updated_at } : old,
        );
      }
      void queryClient.invalidateQueries({ queryKey: ['products'] });
    },
  });
}

/** One queue per product for its section-review saves AND its approve: each waits for the one
 *  before it. So saves land in the order they were made, and an approve never overtakes a save
 *  still in flight — which means a save can only hit "already approved" when someone ELSE
 *  approved (or the product was archived), a real failure worth telling the reviewer about. */
const sectionReviewScope = (productId: number | null) => ({ id: `section-review-${productId}` });

/** Save one in-progress section-review edit to the server as it's committed. On success the
 *  cached proposal's `section_edits` is patched in place (no refetch of the large proposal),
 *  so a later remount of the review restores the edit from the cache too. */
export function useSaveSectionEdit(productId: number | null) {
  const queryClient = useQueryClient();
  return useMutation({
    scope: sectionReviewScope(productId),
    // Here, not in mutate()'s options: the save fired while leaving the page (the editor
    // commits on unmount) runs after the screen is gone, and only hook-level callbacks still
    // fire then.
    onError: (err) => {
      toast.error(
        err instanceof ApiConflictError
          ? // someone approved it, the spec was re-uploaded, or the product was archived
            "Your edit wasn't saved because this review has changed. Reload the page to see its current state."
          : err instanceof ApiError && err.status === 413
            ? err.message
            : "Couldn't save your edit. Check your connection before leaving this page.",
      );
    },
    mutationFn: async (edit: SectionEdit) => {
      if (productId == null) throw new Error('productId is null');
      return saveSectionEdit(productId, edit);
    },
    onSuccess: (_res, edit) => {
      if (productId == null) return;
      queryClient.setQueryData<ExtractionProposal>(proposalKey(productId), (prev) =>
        prev
          ? { ...prev, section_edits: { ...(prev.section_edits ?? {}), [edit.order]: edit.markdown } }
          : prev,
      );
    },
  });
}

/** Approve via the SECTION review — the kept sections become the product's spec, with
 *  inline edits to flagged sections applied. */
export function useApproveSections(productId: number | null) {
  const queryClient = useQueryClient();
  return useMutation({
    scope: sectionReviewScope(productId), // waits for any section-edit save still in flight
    mutationFn: async ({ keptOrders, edits }: { keptOrders: number[]; edits: SectionEdit[] }) => {
      if (productId == null) throw new Error('productId is null');
      return approveSections(productId, keptOrders, edits);
    },
    onSuccess: () => {
      if (productId != null) {
        void queryClient.invalidateQueries({ queryKey: proposalKey(productId) });
        void queryClient.invalidateQueries({ queryKey: ['products'] });
      }
    },
  });
}
